import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';

const MedicalAdminBatchSelection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoading(true);
      // Medical admins can see ALL batches, not just their own
      const response = await api.get('/batches');
      setBatches(response.data.batches || []);
    } catch (err) {
      console.error('Error loading batches:', err);
      setError('Failed to load batches: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = (batchId) => {
    navigate(`/medical-admin/assign-cases-to-batch/${batchId}`);
  };

  const getStatusBadge = (batch) => {
    if (batch.is_expired) {
      return <span className="badge bg-danger">Expired</span>;
    } else if (batch.is_active) {
      return <span className="badge bg-success">Active</span>;
    } else {
      return <span className="badge bg-warning">Scheduled</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Filter batches based on search term and status
  const filteredBatches = batches.filter(batch => {
    const matchesSearch =
      batch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (batch.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(batch.id).includes(searchTerm);
    
    let statusMatch = true;
    if (batchStatusFilter === 'active') {
      statusMatch = batch.is_active;
    } else if (batchStatusFilter === 'scheduled') {
      statusMatch = !batch.is_active && !batch.is_expired;
    } else if (batchStatusFilter === 'expired') {
      statusMatch = batch.is_expired;
    }
    
    return matchesSearch && statusMatch;
  });

  if (loading) {
    return (
      <ResearchLayout pageTitle="Select Batch to Assign Cases">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading your batches...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Select Batch to Assign Cases">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-1">
                  <i className="fas fa-layer-group me-2"></i>
                  Select a Batch
                </h4>
                <small className="opacity-75">
                  Choose a batch to assign cases to
                </small>
              </div>
              <div className="card-body">
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                  </div>
                )}

                {/* Filter Controls */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search batches by name, description, or ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setSearchTerm('')}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                    {searchTerm && (
                      <div className="small text-muted mt-1">
                        <i className="fas fa-filter me-1"></i>
                        Showing {filteredBatches.length} of {batches.length} batches
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={batchStatusFilter}
                      onChange={(e) => setBatchStatusFilter(e.target.value)}
                    >
                      <option value="all">All Batches</option>
                      <option value="active">Active Batches</option>
                      <option value="scheduled">Scheduled Batches</option>
                      <option value="expired">Expired Batches</option>
                    </select>
                  </div>
                </div>

                {batchStatusFilter !== 'all' && (
                  <div className="alert alert-info">
                    <i className="fas fa-filter me-2"></i>
                    <strong>Batch Status Filter:</strong>
                    Showing {batchStatusFilter} batches
                    {searchTerm && ` matching "${searchTerm}"`}
                  </div>
                )}

                {filteredBatches.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-layer-group fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Batches Found</h6>
                    <p className="text-muted">
                      {searchTerm || batchStatusFilter !== 'all'
                        ? 'No batches match your current filters.'
                        : "You don't have any batches created yet."
                      }
                    </p>
                    {(searchTerm || batchStatusFilter !== 'all') ? (
                      <button
                        className="btn btn-outline-secondary me-2"
                        onClick={() => {
                          setSearchTerm('');
                          setBatchStatusFilter('all');
                        }}
                      >
                        <i className="fas fa-refresh me-1"></i>
                        Clear Filters
                      </button>
                    ) : (
                      <a href="/medical-admin/batch-creation" className="btn btn-primary">
                        <i className="fas fa-plus me-2"></i>
                        Create Your First Batch
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="row">
                    {filteredBatches.map((batch) => (
                      <div key={batch.id} className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 border-2">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h5 className="card-title mb-0">
                                <i className="fas fa-layer-group text-primary me-2"></i>
                                {batch.name}
                              </h5>
                              {getStatusBadge(batch)}
                            </div>
                            
                            <p className="card-text text-muted mb-3">
                              {batch.description || 'No description provided'}
                            </p>
                            
                            <div className="row mb-3">
                              <div className="col-6">
                                <small className="text-muted">Start Date</small>
                                <div className="fw-bold small">
                                  {formatDate(batch.start_date)}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">End Date</small>
                                <div className="fw-bold small">
                                  {formatDate(batch.end_date)}
                                </div>
                              </div>
                            </div>
                            
                            <div className="row mb-3">
                              <div className="col-4">
                                <small className="text-muted">Cases in Batch</small>
                                <div className="fw-bold small text-primary">
                                  <i className="fas fa-file-medical me-1"></i>
                                  {batch.case_count || 0} Cases
                                </div>
                              </div>
                              <div className="col-4">
                                <small className="text-muted">Protocols in Batch</small>
                                <div className="fw-bold small text-warning">
                                  <i className="fas fa-clipboard-list me-1"></i>
                                  {batch.protocol_count || 0} Protocols
                                </div>
                              </div>
                              <div className="col-4">
                                <small className="text-muted">Doctors Assigned</small>
                                <div className="fw-bold small text-info">
                                  <i className="fas fa-user-md me-1"></i>
                                  {batch.doctor_count || 0} Doctors
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-grid">
                              <button
                                className="btn btn-primary"
                                onClick={() => handleBatchSelect(batch.id)}
                              >
                                <i className="fas fa-file-medical me-2"></i>
                                Assign Cases to This Batch
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminBatchSelection;